import { useState, useCallback } from 'react';
import { api } from '../utils/api';
import type { AdminStats, AdminPhotosResponse, Settings, AuditLogResponse } from '../types';

export function useAdminStats() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.admin.getStats();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Statistiken');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { stats, isLoading, error, load };
}

export function useAdminPhotos() {
  const [photos, setPhotos] = useState<AdminPhotosResponse['photos']>([]);
  const [pagination, setPagination] = useState<AdminPhotosResponse['pagination']>({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (page = 1, limit = 100, order: 'asc' | 'desc' = 'desc') => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.admin.getPhotos(page, limit, order);
      setPhotos(data.photos);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Fotos');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deletePhoto = useCallback(async (id: number) => {
    setError(null);
    try {
      await api.admin.deletePhoto(id);
      setPhotos(prev => prev.filter(p => p.id !== id));
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen');
      throw err;
    }
  }, []);

  const deleteMultiple = useCallback(async (ids: number[]) => {
    setError(null);
    try {
      const result = await api.admin.deleteMultiplePhotos(ids);
      setPhotos(prev => prev.filter(p => !ids.includes(p.id)));
      setPagination(prev => ({ ...prev, total: prev.total - result.deleted }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen');
      throw err;
    }
  }, []);

  return { photos, pagination, isLoading, error, load, deletePhoto, deleteMultiple };
}

export function useAdminSettings() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.admin.getSettings();
      setSettings(data.settings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Einstellungen');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const save = useCallback(async (newSettings: Partial<Settings>) => {
    setIsSaving(true);
    setError(null);
    try {
      const data = await api.admin.updateSettings(newSettings);
      setSettings(data.settings);
      return data.settings;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  return { settings, isLoading, isSaving, error, load, save };
}

export function useAdminAuditLog() {
  const [logs, setLogs] = useState<AuditLogResponse['logs']>([]);
  const [pagination, setPagination] = useState<AuditLogResponse['pagination']>({ page: 1, limit: 50 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (page = 1, limit = 50) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.admin.getAuditLog(page, limit);
      setLogs(data.logs);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden des Logs');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { logs, pagination, isLoading, error, load };
}

export function useAdminActions() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string, isAdmin: boolean) => {
    setIsLoading(true);
    setError(null);
    try {
      return await api.admin.changePassword(currentPassword, newPassword, isAdmin);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passwortänderung fehlgeschlagen');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const cleanup = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      return await api.admin.cleanup();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bereinigung fehlgeschlagen');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteAll = useCallback(async (confirmText: string) => {
    setIsLoading(true);
    setError(null);
    try {
      return await api.admin.deleteAll(confirmText);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, changePassword, cleanup, deleteAll };
}